import { baseApi } from '../../services/baseApi';
import { ApiEndPoints } from '../../constants/apiEndPoints';

/**
 * Read-only territory (location) hierarchy endpoints, injected into the shared
 * baseApi. The step-2 tree calls these one level at a time, passing the parent's
 * code to fetch its children:
 *
 *   state → parliament(?state) → assembly(?pc)
 *     ├─ RURAL:  gram-panchayat(?ac_code__startswith) → village(?gram_panchayat_lgd_code&type=PRI) → ps-village-map(?village_result_code)
 *     └─ URBAN:  block-ulb(?type=ULB) → ward(?block_ulb_code) → village(?ward_code&type=ULB) → ps-village-map(?village_result_code)
 */

const T = ApiEndPoints.territory;

/** DRF list responses may be a plain array or a paginated `{ results }` envelope. */
type Listish<T> = T[] | { results?: T[] };
const asList = <T>(r: Listish<T>): T[] => (Array.isArray(r) ? r : (r?.results ?? []));

export interface EcLevelDto {
  id: number;
  level_name?: string;
  level_rank?: number;
}
export interface StateDto {
  id: number;
  name: string;
  state_id?: number;
  state_code?: string;
  state_cd?: string;
  type?: string;
}
export interface ParliamentDto {
  id: number;
  name: string;
  pc_code?: string;
  state?: number;
}
export interface AssemblyDto {
  id: number;
  name: string;
  ac_code?: string;
  pc?: number;
}
export interface GramPanchayatDto {
  gram_panchayat_pk: number;
  name_en: string;
  gram_panchayat_lgd_code?: string;
  ac_code?: string;
}
export interface VillageDto {
  village_pk: number;
  name_en: string;
  village_result_code?: string;
  type?: string;
  total_electors?: number;
  ac_code?: string;
  gram_panchayat_lgd_code?: string;
  block_ulb_code?: string;
  ward_code?: string;
}
export interface BlockUlbDto {
  block_pk: number;
  name_en: string;
  block_ulb_code?: string;
  type?: string;
}
export interface WardDto {
  ward_pk: number;
  ward_name_en: string;
  ward_code?: string;
  block_ulb_code?: string;
}
export interface PsVillageMapDto {
  ps_code: string;
  booth_name?: string;
  booth_name_hi?: string;
  village_result_code?: string;
  village_code?: string;
}
/** A polling station (booth) as returned by /territory/booth/ (scoped by assembly). */
export interface PollingStationDto {
  id: number;
  name: string;
  name_v2?: string | null;
  ps_code?: string | null;
  assembly_constituency?: number;
  partNumber?: number | null;
}

/**
 * A booth's full EC location from /territory/booth-info/?ps_code=… — used to build the
 * step-4 EC structure for a booth clicked in the Internal Organization panel.
 */
export interface BoothInfoDto {
  booth?: {
    name?: string | null;
    name_hi?: string | null;
    ps_code?: string | null;
    address?: string | null;
    part_number?: number | null;
  } | null;
  assembly?: { name?: string | null; ac_code?: string | null } | null;
  parliament?: { name?: string | null; pc_code?: string | null } | null;
  district?: { name?: string | null } | null;
  state?: { name?: string | null } | null;
  village?: { name?: string | null; village_result_code?: string | null } | null;
  gram_panchayat?: { name?: string | null; gram_panchayat_lgd_code?: string | null } | null;
}

export const territoryApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getEcLevels: build.query<EcLevelDto[], void>({
      query: () => ({ url: `${T.ecLevel}/` }),
      transformResponse: (r: Listish<EcLevelDto>) => asList(r),
    }),
    getStates: build.query<StateDto[], { search?: string } | void>({
      query: (arg) => ({
        url: `${T.state}/`,
        params: arg?.search ? { search: arg.search } : {},
      }),
      transformResponse: (r: Listish<StateDto>) => asList(r),
    }),
    getParliaments: build.query<ParliamentDto[], { state: number | string }>({
      query: ({ state }) => ({ url: `${T.parliament}/`, params: { state } }),
      transformResponse: (r: Listish<ParliamentDto>) => asList(r),
    }),
    getAssemblies: build.query<AssemblyDto[], { pc: number | string }>({
      query: ({ pc }) => ({ url: `${T.assembly}/`, params: { pc } }),
      transformResponse: (r: Listish<AssemblyDto>) => asList(r),
    }),
    // Rural
    getGramPanchayats: build.query<GramPanchayatDto[], { acCode: string }>({
      query: ({ acCode }) => ({ url: `${T.gramPanchayat}/`, params: { ac_code__startswith: acCode } }),
      transformResponse: (r: Listish<GramPanchayatDto>) => asList(r),
    }),
    getRuralVillages: build.query<VillageDto[], { gpCode: string }>({
      query: ({ gpCode }) => ({
        url: `${T.village}/`,
        params: { gram_panchayat_lgd_code: gpCode, type: 'PRI' },
      }),
      transformResponse: (r: Listish<VillageDto>) => asList(r),
    }),
    // Urban
    getUlbs: build.query<BlockUlbDto[], void>({
      query: () => ({ url: `${T.blockUlb}/`, params: { type: 'ULB' } }),
      transformResponse: (r: Listish<BlockUlbDto>) => asList(r),
    }),
    getWards: build.query<WardDto[], { ulbCode: string }>({
      query: ({ ulbCode }) => ({ url: `${T.ward}/`, params: { block_ulb_code: ulbCode } }),
      transformResponse: (r: Listish<WardDto>) => asList(r),
    }),
    getUrbanVillages: build.query<VillageDto[], { wardCode: string }>({
      query: ({ wardCode }) => ({ url: `${T.village}/`, params: { ward_code: wardCode, type: 'ULB' } }),
      transformResponse: (r: Listish<VillageDto>) => asList(r),
    }),
    // Booths (both paths)
    getBooths: build.query<PsVillageMapDto[], { villageResultCode: string }>({
      query: ({ villageResultCode }) => ({
        url: `${T.psVillageMap}/`,
        params: { village_result_code: villageResultCode },
      }),
      transformResponse: (r: Listish<PsVillageMapDto>) => asList(r),
    }),
    // Full EC location of one booth (by ps_code) — used to build the EC structure for it.
    getBoothInfo: build.query<BoothInfoDto, { psCode: string }>({
      query: ({ psCode }) => ({ url: `${T.boothInfo}/`, params: { ps_code: psCode } }),
      transformResponse: (r: BoothInfoDto | BoothInfoDto[] | { results?: BoothInfoDto[] }) => {
        if (Array.isArray(r)) return r[0] ?? {};
        const results = (r as { results?: BoothInfoDto[] })?.results;
        if (Array.isArray(results)) return results[0] ?? {};
        return (r as BoothInfoDto) ?? {};
      },
    }),
    // All booths under one assembly, in a single (paginated) call.
    getBoothsByAssembly: build.query<PollingStationDto[], { assembly: number | string }>({
      query: ({ assembly }) => ({
        url: `${T.booth}/`,
        params: { assembly_constituency: assembly, page_size: 2000 },
      }),
      transformResponse: (r: Listish<PollingStationDto>) => asList(r),
    }),

    // --- Reverse-lookup endpoints (edit-mode auto-expand to a saved area) ---
    getAssemblyByAc: build.query<AssemblyDto[], { acCode: string }>({
      query: ({ acCode }) => ({ url: `${T.assembly}/`, params: { ac_code: acCode } }),
      transformResponse: (r: Listish<AssemblyDto>) => asList(r),
    }),
    getParliamentById: build.query<ParliamentDto, { id: number | string }>({
      query: ({ id }) => ({ url: `${T.parliament}/${id}/` }),
    }),
    getParliamentByPcCode: build.query<ParliamentDto[], { pcCode: string }>({
      query: ({ pcCode }) => ({ url: `${T.parliament}/`, params: { pc_code: pcCode } }),
      transformResponse: (r: Listish<ParliamentDto>) => asList(r),
    }),
    getGramByLgd: build.query<GramPanchayatDto[], { lgd: string }>({
      query: ({ lgd }) => ({
        url: `${T.gramPanchayat}/`,
        params: { gram_panchayat_lgd_code: lgd },
      }),
      transformResponse: (r: Listish<GramPanchayatDto>) => asList(r),
    }),
    getVillagesByAc: build.query<VillageDto[], { acCode: string; type: 'PRI' | 'ULB' }>({
      query: ({ acCode, type }) => ({
        url: `${T.village}/`,
        params: { ac_code: acCode, type, page_size: 200 },
      }),
      transformResponse: (r: Listish<VillageDto>) => asList(r),
    }),
    // All villages under an assembly (ac_code is granular, so match by prefix) — one call.
    getVillagesByAcPrefix: build.query<VillageDto[], { acCode: string; type: 'PRI' | 'ULB' }>({
      query: ({ acCode, type }) => ({
        url: `${T.village}/`,
        params: { ac_code__startswith: acCode, type, page_size: 2000 },
      }),
      transformResponse: (r: Listish<VillageDto>) => asList(r),
    }),
    getPsVillageByResult: build.query<PsVillageMapDto[], { resultCode: string }>({
      query: ({ resultCode }) => ({
        url: `${T.psVillageMap}/`,
        params: { village_result_code: resultCode },
      }),
      transformResponse: (r: Listish<PsVillageMapDto>) => asList(r),
    }),
    getPsVillageByPs: build.query<PsVillageMapDto[], { psCode: string }>({
      query: ({ psCode }) => ({ url: `${T.psVillageMap}/`, params: { ps_code: psCode } }),
      transformResponse: (r: Listish<PsVillageMapDto>) => asList(r),
    }),
  }),
});

export const { useGetEcLevelsQuery } = territoryApi;
